import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

/**
 * Shared, read-only "this is what your customer will see" email preview.
 *
 * Used by both `RebookPreviewDialog` and `ReviewRequestPreviewDialog` (and
 * any future customer-facing email actions) so the visual contract — header
 * card with From/Reply-To/Subject + a body slot — is identical across the
 * app. This is *display only*: opening the dialog must NEVER trigger any
 * network call. The actual send button lives next to the trigger and is
 * untouched by this component.
 *
 * Props:
 *   - open / onOpenChange: Shadcn Dialog controlled props.
 *   - subject:             pre-resolved subject line.
 *   - fromLabel:           "Tasklio AI <support@tasklio.co>" by default;
 *                          overridable so unit tests / preview surfaces can
 *                          inject env-specific identifiers without us
 *                          fetching server config in the client.
 *   - replyTo:             pre-resolved Reply-To string. Caller is the
 *                          source of truth — should mirror the backend's
 *                          `_resolve_customer_reply_to`.
 *   - sendButtonLabel:     name of the actual send button on the page, used
 *                          to remind the owner what will trigger the send
 *                          ("Nothing is sent until you click X").
 *   - testidPrefix:        unique prefix for `data-testid` attributes so
 *                          multiple preview types can coexist on a page
 *                          (e.g. `rebook-preview` and `review-preview`).
 *   - children:            the body slot — whatever the specific email
 *                          template should render between the header card
 *                          and the close of the dialog.
 */
export const EmailPreviewDialog = ({
  open,
  onOpenChange,
  subject,
  fromLabel = "Tasklio AI <support@tasklio.co>",
  replyTo,
  sendButtonLabel,
  testidPrefix,
  children,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Email preview</DialogTitle>
          <DialogDescription>
            This is exactly what the customer will receive. Nothing is sent
            until you click <span className="font-medium">{sendButtonLabel}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3 text-sm">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 space-y-1.5">
            <div className="grid grid-cols-[80px_1fr] gap-x-2">
              <span className="text-xs uppercase tracking-wide text-gray-500">From</span>
              <span className="text-gray-800" data-testid={`${testidPrefix}-from`}>
                {/* Render the literal "<...>" wrapped so the angle brackets
                    display verbatim in JSX without HTML entity workarounds. */}
                {fromLabel}
              </span>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-x-2">
              <span className="text-xs uppercase tracking-wide text-gray-500">Reply-To</span>
              <span
                className="text-gray-800 break-all"
                data-testid={`${testidPrefix}-reply-to`}
              >
                {replyTo}
              </span>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-x-2">
              <span className="text-xs uppercase tracking-wide text-gray-500">Subject</span>
              <span
                className="font-medium text-gray-900"
                data-testid={`${testidPrefix}-subject`}
              >
                {subject}
              </span>
            </div>
          </div>

          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Resolve the Reply-To address customers see on customer-facing emails.
 * Mirrors backend `_resolve_customer_reply_to`. Exported so every preview
 * dialog uses the exact same fallback chain.
 */
export const resolveCustomerReplyTo = (user) =>
  (user?.customer_reply_email || "").trim() ||
  (user?.email || "").trim() ||
  "support@tasklio.co";
